/**
 * Fields filled with the on-screen keyboard. Each field is sent when confirmed (a button with the
 * field's data plus `value`), so the backend — and the voice — always know what is filled in.
 * `secret` fields are masked; nothing here is ever read aloud by the kiosk itself.
 */
import { useState } from 'react';
import { z } from 'zod';
import { defineWidget } from '../contract';
import { Icon } from '../../ui-kit/Icon';
import { Keyboard } from '../../ui-kit/Keyboard';
import { Panel } from '../../ui-kit/Panel';
import css from '../Widgets.module.css';

const schema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  icon: z.string().optional(),
  fields: z.array(z.object({
    id: z.string(),
    label: z.string(),
    value: z.string().nullable().default(null),
    kind: z.enum(['text', 'email', 'tel']).default('text'),
    required: z.boolean().default(false),
    sensitivity: z.enum(['public', 'personal', 'secret']).default('personal'),
    wide: z.boolean().default(false),
    send: z.object({ button: z.string(), data: z.record(z.string(), z.unknown()).default({}) }),
  })).min(1),
  doneLabel: z.string(),
  spaceLabel: z.string(),
  footer: z.string().optional(),
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const form = defineWidget({
  key: 'form',
  schema,
  Component: function FormWidget({ props, ctx, api }) {
    const [editing, setEditing] = useState<string | null>(null);
    const [text, setText] = useState('');
    const valueOf = (id: string, fallback: string | null) => {
      const drafted = ctx.draft[id];
      return typeof drafted === 'string' ? drafted : fallback ?? '';
    };
    const field = props.fields.find((f) => f.id === editing);
    const commit = () => {
      if (!field) return;
      const value = text.trim();
      if (field.kind === 'email' && value && !EMAIL.test(value)) return;
      api.setDraft({ [field.id]: value });
      if (value) api.press(field.send.button, { ...field.send.data, value });
      const next = props.fields.find((f) => f.required && f.id !== field.id && !valueOf(f.id, f.value));
      setEditing(next?.id ?? null);
      setText(next ? valueOf(next.id, next.value) : '');
    };
    return (
      <Panel title={props.title} subtitle={props.subtitle} icon={props.icon} footer={field ? undefined : props.footer} footerIcon="mic">
        <div className={css.formGrid}>
          {props.fields.map((f) => {
            const value = valueOf(f.id, f.value);
            const shown = f.sensitivity === 'secret' && value ? '•'.repeat(Math.min(10, value.length)) : value;
            return (
              <button key={f.id} className={`${css.field} ${f.wide ? css.wide : ''} ${editing === f.id ? css.editing : ''} ${value ? css.filled : ''}`}
                onClick={() => { setEditing(f.id); setText(value); }}>
                <span className={css.fieldLabel}>{f.label}{f.required ? ' *' : ''}</span>
                <span className={css.fieldValue}>{shown || '—'}</span>
                {value && <Icon name="check" size={24} className={css.fieldOk} />}
              </button>
            );
          })}
        </div>
        {field && (
          <div className={css.editor}>
            <div className={css.editorLine}>
              <span className={css.fieldLabel}>{field.label}</span>
              <output className={`${css.editorValue} ${field.kind === 'email' && text && !EMAIL.test(text) ? css.invalid : ''}`}>
                {field.sensitivity === 'secret' ? '•'.repeat(text.length) : text}<span className={css.caret} />
              </output>
              <button className={css.step} onClick={() => setEditing(null)} aria-label="close"><Icon name="close" /></button>
            </div>
            <Keyboard key={field.id} layout={field.kind} value={text} onChange={setText} onEnter={commit} spaceLabel={props.spaceLabel} doneLabel={props.doneLabel} />
          </div>
        )}
      </Panel>
    );
  },
});
